import {useEffect, useMemo, useState} from 'react'
import {Heading} from '@/components/tailwind/heading'
import {MemberAddressListItem} from '@/models/member-address-list'
import {exportAddressListExcel, getAddressList} from '@/services/member-address-list-services'
import {useAuth} from '@/context/use-auth'
import {useReportsTranslation, useCommonTranslation} from '@/i18n/hooks'
import {Text} from '@/components/tailwind/text'
import {Input, InputGroup} from '@/components/tailwind/input'
import {MagnifyingGlassIcon} from '@heroicons/react/16/solid'
import {MemberAddressListDataTable} from './member-address-list-data-table'
import {createAddressListColumns} from './member-address-list-columns'
import {Button} from '@/components/tailwind/button'
import {FileSpreadsheet} from 'lucide-react'
import {useToast} from '@/hooks/use-toast'
import log from '@/lib/logger'

function MemberAddressListPage() {
    const [members, setMembers] = useState<MemberAddressListItem[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [searchTerm, setSearchTerm] = useState('')
    const {token} = useAuth()
    const {t} = useReportsTranslation()
    const {t: tCommon} = useCommonTranslation()
    const {toast} = useToast()

    useEffect(() => {
        async function fetchData() {
            if (!token) {
                log.error('No token available for fetching address list')
                setLoading(false)
                return
            }

            try {
                setLoading(true)
                setError(null)
                const data = await getAddressList(token)
                setMembers(data)
            } catch (err) {
                log.error('Error fetching address list:', err)
                setError(t('addressList.error'))
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [token])

    const columns = useMemo(() => createAddressListColumns(), [])

    async function handleExportExcel() {
        if (!token) return

        try {
            const {blob, filename} = await exportAddressListExcel(token)
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = filename
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
        } catch (err) {
            log.error('Error downloading Excel:', err)
            toast({
                title: t('addressList.export.error'),
                description: t('addressList.export.errorDescription'),
                variant: 'destructive'
            })
        }
    }

    if (loading) {
        return (
            <div className="container mx-auto py-6">
                <Text>{tCommon('loading')}</Text>
            </div>
        )
    }

    if (error) {
        return (
            <div className="container mx-auto py-6">
                <Text className="text-red-500">{error}</Text>
            </div>
        )
    }

    return (
        <div className="container mx-auto py-6 space-y-6">
            <div className="flex items-start justify-between">
                <div>
                    <Heading>{t('addressList.title')}</Heading>
                    <Text>{t('addressList.description')}</Text>
                </div>
                <Button
                    plain
                    onClick={handleExportExcel}
                    className="flex items-center gap-2"
                    title={t('addressList.export.excel')}
                >
                    <FileSpreadsheet className="h-4 w-4"/>
                    <span className="hidden sm:inline">{t('addressList.export.excel')}</span>
                </Button>
            </div>

            <div>
                <InputGroup>
                    <MagnifyingGlassIcon/>
                    <Input
                        type="text"
                        placeholder={t('addressList.search.placeholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        aria-label={t('addressList.search.ariaLabel')}
                    />
                </InputGroup>
            </div>

            {members.length === 0 ? (
                <Text className="italic text-gray-500">{t('addressList.noMembers')}</Text>
            ) : (
                <MemberAddressListDataTable
                    columns={columns}
                    data={members}
                    searchTerm={searchTerm}
                />
            )}
        </div>
    )
}

export default MemberAddressListPage
